import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { googleAuthApi } from "@/api/googleAuthApi";
import { useAuthContext } from "@/contexts/AuthContext";
import { TotpChallengeStep } from "./TotpChallengeStep";

import "./LoginPage.css";

export default function LoginGoogleCompletePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isInitializing, applyGrants, verifyTotpLogin } = useAuthContext();
  const [message, setMessage] = useState("Completing Google sign-in…");
  const [totpSession, setTotpSession] = useState("");
  const [totpError, setTotpError] = useState("");
  const [totpLoading, setTotpLoading] = useState(false);
  const finishedRef = useRef(false);

  useEffect(() => {
    if (!isInitializing && isAuthenticated) {
      navigate("/", { replace: true });
      return undefined;
    }

    const state = searchParams.get("state") || "";
    if (!state) {
      setMessage("Invalid sign-in session. Return to login and try again.");
      return undefined;
    }

    if (finishedRef.current || totpSession) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await googleAuthApi.completeLogin(state);
        if (cancelled || finishedRef.current) {
          return;
        }
        if (result.status === "totp_required") {
          setTotpSession(result.totp_session);
          setMessage("");
          return;
        }
        finishedRef.current = true;
        applyGrants(result, true);
        window.location.href = "/";
      } catch (err) {
        if (!cancelled && !finishedRef.current) {
          const msg = err instanceof Error ? err.message : "Google sign-in failed.";
          setMessage(msg);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, isAuthenticated, isInitializing, navigate, applyGrants, totpSession]);

  const handleTotpSubmit = async (code) => {
    setTotpLoading(true);
    setTotpError("");
    try {
      await verifyTotpLogin({ totp_session: totpSession, code, rememberMe: true });
      window.location.href = "/";
    } catch (err) {
      setTotpError(err instanceof Error ? err.message : "Invalid authenticator code.");
    } finally {
      setTotpLoading(false);
    }
  };

  if (totpSession) {
    return (
      <div className="login-page">
        <div className="login-page__left">
          <div className="login-page__main">
            <div className="login-page__card">
              <h1 className="login-page__title">Two-factor authentication</h1>
              <p className="login-page__subtitle">
                Enter the 6-digit code from your authenticator app.
              </p>
              <TotpChallengeStep
                onSubmit={handleTotpSubmit}
                onBack={() => navigate("/login", { replace: true })}
                loading={totpLoading}
                error={totpError}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page login-page--boot" role="status">
      <span className="login-page__boot-text">{message}</span>
      <p className="login-page__subtitle" style={{ marginTop: "1rem" }}>
        <a href="/login">Back to sign in</a>
      </p>
    </div>
  );
}
